import React from 'react'
import {
  FieldValues, Path, useController, useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DEFAULT_FIELD_RULE, FieldRule } from '../../features/declaration/models/fieldRules'

export type RadioOption = {
  value: string | number
  label: React.ReactNode
  disabled?: boolean
}

export type FormInputProps<T extends FieldValues> = {
  field: Path<T>
  labelKey?: string
  label?: React.ReactNode
  // placeholder?: string
  // helperText?: React.ReactNode
  // className?: string
  /** Optional RHF validation rules */
  // rules?: RegisterOptions<T, Path<T>>
  /** Optional external/business rule errors keyed by path */
  // businessErrors?: BusinessErrors
  /** Radio-only options */
  radioOptions?: RadioOption[]
  fieldRule?: FieldRule
  defaultValue?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'checked'>

function RadioGroup<TFieldValues extends FieldValues = FieldValues>({
  field: name,
  radioOptions,
  fieldRule = DEFAULT_FIELD_RULE,
  defaultValue,
}: FormInputProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>()
  const { t } = useTranslation()

  const {
    field,
    fieldState,
  } = useController<TFieldValues>({
    name,
    control,
  })

  const current = (field.value ?? defaultValue) as string

  return (
    <React.Fragment key={`${field.name}`}>
      {radioOptions?.map((option) => {
        const rid = `${field.name}_radio_${option.value}`

        return (
          <div className="form-check form-check-inline mb-0" key={rid}>
            <input
              id={rid}
              type="radio"
              name={field.name}
              value={String(option.value)}
              checked={current === option.value}
              className={`form-check-input ${fieldState.invalid ? 'is-invalid' : ''
              } ${
                fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
              }`}
              disabled={fieldRule.requirement === 'forbidden'}
              onChange={() => field.onChange(option.value)}
              onBlur={field.onBlur}
              ref={field.ref}
            />
            <label className="form-check-label" htmlFor={rid}>
              {t(option.label)}
            </label>
          </div>
        )
      })}
    </React.Fragment>
  )
}

RadioGroup.defaultProps = {
  labelKey: '',
  label: undefined,
  radioOptions: [],
  fieldRule: DEFAULT_FIELD_RULE,
  defaultValue: '',
}

export default RadioGroup
